import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { createEquipment } from "../models/models";
import { equipmentCategories, equipmentSubtypes } from "../utils/constants";
import { hasCapability } from "../utils/capabilities";
import { isNativeMobile } from "../utils/platform";
import BackButton from "../components/BackButton";
import DateWithYearJump from "../components/DateWithYearJump";
import TypedConfirmModal from "../components/TypedConfirmModal";

export default function LocationDetailPage({
  customer,
  location,
  actions,
  role,
  capabilities,
  orgId,
  setSelectedLocation,
  setSelectedEquipment,
  onShowEquipmentByStatus,
}) {
  const canAddEquipment = hasCapability(role, capabilities, "addEquipment");
  const canEditEquipment = hasCapability(role, capabilities, "editEquipment");
  const canDeleteEquipment = hasCapability(role, capabilities, "deleteEquipment");
  const canShareBuilding = hasCapability(role, capabilities, "editLocations");
  // Only a facility executive assigns staff to buildings (controls who can
  // see this building on their facility boards).
  const canAssignStaff = role === "executive";
  const mobile = isNativeMobile();

  // Always source from the freshly-derived customer prop; never fall back
  // to the snapshot taken at click-time, which could be stale after the
  // user edits something inside this building. If the live lookup misses
  // (race during nav, building removed, access revoked), render a Loading
  // / unavailable state instead of stale data.
  const currentLocation =
    customer?.locations?.find((l) => l.id === location?.id) || null;

  const genCode = () =>
    Math.random().toString(36).slice(2, 10).toUpperCase();

  // Facility staff (for the executive's "Assign staff" picker). Only loaded
  // when this user can assign and we have an org to scope to. The listener
  // depends on currentLocation.id too so navigating into a different
  // building rebuilds it — defends against silent listener stalls
  // (previously you had to bounce home + back to "regain" the picker).
  const [orgUsers, setOrgUsers] = useState([]);
  const [orgUsersErr, setOrgUsersErr] = useState("");
  useEffect(() => {
    if (!canAssignStaff || !orgId) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setOrgUsersErr("");
      },
      (err) => {
        console.error("LocationDetailPage orgUsers listener:", err);
        setOrgUsersErr(err.message);
      }
    );
    return unsub;
  }, [canAssignStaff, orgId, currentLocation?.id]);

  const toggleAssigned = (uid) => {
    const current = currentLocation.assignedUsers || [];
    const next = current.includes(uid)
      ? current.filter((x) => x !== uid)
      : [...current, uid];
    actions.updateLocation(currentLocation.id, { assignedUsers: next });
  };

  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [showShareBuilding, setShowShareBuilding] = useState(false);
  const [equipmentCategory, setEquipmentCategory] = useState("HVAC");
  const [subType, setSubType] = useState("");
  const [equipmentName, setEquipmentName] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [modelNumber, setModelNumber] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [startupDate, setStartupDate] = useState("");
  const [quickNotes, setQuickNotes] = useState("");
  const [startupInstructions, setStartupInstructions] = useState("");
  const [partsList, setPartsList] = useState("");
  const [lastPmDate, setLastPmDate] = useState("");
  const [pmFrequencyDays, setPmFrequencyDays] = useState("");

  // Hard-delete confirmation: holds the equipment doc queued for the
  // typed-confirm modal. Cascades service records via deleteEquipment in
  // useOrgData. Type-DELETE matches the same guardrail used for service-
  // record deletes (see TypedConfirmModal).
  const [pendingEquipmentDelete, setPendingEquipmentDelete] = useState(null);
  const [equipmentDeleteBusy, setEquipmentDeleteBusy] = useState(false);

  const [editingEquipmentId, setEditingEquipmentId] = useState(null);
  const [editEquipmentName, setEditEquipmentName] = useState("");
  const [editEquipmentCategory, setEditEquipmentCategory] = useState("HVAC");
  const [editSubType, setEditSubType] = useState("");
  const [editManufacturer, setEditManufacturer] = useState("");
  const [editModelNumber, setEditModelNumber] = useState("");
  const [editSerialNumber, setEditSerialNumber] = useState("");
  const [editStartupDate, setEditStartupDate] = useState("");
  const [editQuickNotes, setEditQuickNotes] = useState("");
  const [editStartupInstructions, setEditStartupInstructions] = useState("");
  const [editPartsList, setEditPartsList] = useState("");
  const [editLastPmDate, setEditLastPmDate] = useState("");
  const [editPmFrequencyDays, setEditPmFrequencyDays] = useState("");

  // Persist which category sections are expanded across the
  // LocationDetailPage → EquipmentDetailPage → Back round-trip. Without
  // this the openCategories state resets to {} every remount, so the
  // tech who was inside HVAC, tapped a unit, and came back would land on
  // a fully-collapsed page — feels like the back button took them two
  // levels up. Keyed by location.id so each building remembers its own
  // expand state.
  const openCategoriesKey = location?.id
    ? `openCategories:${location.id}`
    : null;
  const [openCategories, setOpenCategories] = useState(() => {
    if (!openCategoriesKey) return {};
    try {
      const stored = sessionStorage.getItem(openCategoriesKey);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  });
  useEffect(() => {
    if (!openCategoriesKey) return;
    try {
      sessionStorage.setItem(
        openCategoriesKey,
        JSON.stringify(openCategories)
      );
    } catch (e) {
      // sessionStorage can throw in private-browsing modes — swallow.
    }
  }, [openCategoriesKey, openCategories]);

  const toggleCategory = (category) =>
    setOpenCategories((prev) => ({ ...prev, [category]: !prev[category] }));

  const addEquipment = async () => {
    if (!equipmentName) return;

    const newEquipment = {
      ...createEquipment(equipmentName),
      category: equipmentCategory,
      subType,
      manufacturer,
      modelNumber,
      serialNumber,
      startupDate,
      quickNotes,
      startupInstructions,
      partsList,
      lastPmDate,
      pmFrequencyDays,
    };

    await actions.addEquipment(customer.id, currentLocation.id, newEquipment);

    setEquipmentName("");
    setSubType("");
    setManufacturer("");
    setModelNumber("");
    setSerialNumber("");
    setStartupDate("");
    setQuickNotes("");
    setStartupInstructions("");
    setPartsList("");
    setLastPmDate("");
    setPmFrequencyDays("");
  };

  const updateEquipment = async () => {
    if (!editEquipmentName || !editingEquipmentId) return;

    await actions.updateEquipment(editingEquipmentId, {
      type: editEquipmentName,
      category: editEquipmentCategory,
      subType: editSubType,
      manufacturer: editManufacturer,
      modelNumber: editModelNumber,
      serialNumber: editSerialNumber,
      startupDate: editStartupDate,
      quickNotes: editQuickNotes,
      startupInstructions: editStartupInstructions,
      partsList: editPartsList,
      lastPmDate: editLastPmDate,
      pmFrequencyDays: editPmFrequencyDays,
    });
    setEditingEquipmentId(null);
    setEditEquipmentName("");
  };

  const startEditingEquipment = (equipment) => {
    setEditingEquipmentId(equipment.id);
    setEditEquipmentName(equipment.type);
    setEditEquipmentCategory(equipment.category || "HVAC");
    setEditSubType(equipment.subType || "");
    setEditManufacturer(equipment.manufacturer || "");
    setEditModelNumber(equipment.modelNumber || "");
    setEditSerialNumber(equipment.serialNumber || "");
    setEditStartupDate(equipment.startupDate || "");
    setEditQuickNotes(equipment.quickNotes || "");
    setEditStartupInstructions(equipment.startupInstructions || "");
    setEditPartsList(equipment.partsList || "");
    setEditLastPmDate(equipment.lastPmDate || "");
    setEditPmFrequencyDays(equipment.pmFrequencyDays || "");
  };

  if (!currentLocation) {
    return (
      <>
        <BackButton onClick={() => setSelectedLocation(null)} />
        <p style={{ fontSize: "13px", color: "#666", marginTop: "8px" }}>
          Loading building…
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={() => setSelectedLocation(null)} />

      <h2 style={{ fontSize: "28px", margin: "8px 0 4px" }}>
        {currentLocation?.name}
      </h2>

      {(() => {
        const eqs = currentLocation?.equipment || [];
        const greenCount = eqs.filter(
          (e) => (e.status || "green") === "green"
        ).length;
        const yellowCount = eqs.filter((e) => e.status === "yellow").length;
        const redCount = eqs.filter((e) => e.status === "red").length;
        // Caution + Disabled tiles drill into EquipmentByStatusPage filtered
        // to THIS location's equipment (parent App.js handler routes to the
        // page). Good tile stays passive — clicking "looks fine, leave me
        // alone" isn't useful.
        const tile = (label, count, bg, color, onClick) => (
          <div
            onClick={onClick}
            style={{
              padding: "6px 12px",
              borderRadius: "12px",
              background: bg,
              color,
              border: `1px solid ${color}`,
              fontSize: "13px",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: onClick && count > 0 ? "pointer" : "default",
              opacity: onClick && count === 0 ? 0.55 : 1,
              userSelect: "none",
            }}
            title={
              onClick && count > 0
                ? `View ${count} ${label.toLowerCase()} unit${count === 1 ? "" : "s"}`
                : undefined
            }
          >
            <span>● {label}</span>
            <strong style={{ fontSize: "15px" }}>{count}</strong>
          </div>
        );
        const openStatusList = (status) => {
          if (!onShowEquipmentByStatus) return;
          onShowEquipmentByStatus(status, eqs);
        };
        return (
          <div
            style={{
              display: "flex",
              gap: "8px",
              flexWrap: "wrap",
              margin: "0 0 14px",
            }}
          >
            {tile("Good", greenCount, "#c9efc9", "#1a7f37")}
            {tile(
              "Caution",
              yellowCount,
              "#fff3cd",
              "#9a6b00",
              yellowCount > 0 ? () => openStatusList("yellow") : undefined
            )}
            {tile(
              "Disabled",
              redCount,
              "#f8d7da",
              "#c12c2c",
              redCount > 0 ? () => openStatusList("red") : undefined
            )}
          </div>
        );
      })()}

      {canAddEquipment && (
        <button
          style={styles.button}
          onClick={() => setShowAddEquipment(!showAddEquipment)}
        >
          {showAddEquipment ? "Hide Add Equipment" : "Add Equipment"}
        </button>
      )}

      {canAddEquipment && showAddEquipment && (
        <div style={styles.card}>
          <br />
          <label>Category: </label>
          <select
            value={equipmentCategory}
            onChange={(e) => {
              setEquipmentCategory(e.target.value);
              setSubType("");
            }}
          >
            {equipmentCategories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <br />
          <label>Type: </label>
          <select
            value={subType}
            onChange={(e) => setSubType(e.target.value)}
          >
            <option value="">Select Type</option>
            {(equipmentSubtypes[equipmentCategory] || []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <br />
          <input
            placeholder="Equipment Name"
            value={equipmentName}
            onChange={(e) => setEquipmentName(e.target.value)}
          />
          <br />
          <input
            placeholder="Manufacturer"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
          />
          <br />
          <input
            placeholder="Model Number"
            value={modelNumber}
            onChange={(e) => setModelNumber(e.target.value)}
          />
          <br />
          <input
            placeholder="Serial Number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
          />
          <br />
          <label>Start-Up Date: </label>
          <DateWithYearJump
            value={startupDate}
            onChange={setStartupDate}
          />
          <br />
          <label>Last PM Date: </label>
          <DateWithYearJump
            value={lastPmDate}
            onChange={setLastPmDate}
            yearsBack={10}
          />
          <br />
          <label>PM Frequency Days: </label>
          <input
            type="number"
            placeholder="Example: 90"
            value={pmFrequencyDays}
            onChange={(e) => setPmFrequencyDays(e.target.value)}
          />
          <br />
          <textarea
            style={styles.textarea}
            placeholder="Startup / Reset Instructions"
            value={startupInstructions}
            onChange={(e) => setStartupInstructions(e.target.value)}
          />
          <br />
          <textarea
            style={styles.textarea}
            placeholder="Parts list"
            value={partsList}
            onChange={(e) => setPartsList(e.target.value)}
          />
          <br />
          <button onClick={addEquipment}>Add Equipment</button>
          <br />
        </div>
      )}

      <h3 style={mobile ? { fontSize: "22px", margin: "20px 0 10px" } : undefined}>
        Equipment
      </h3>

      {equipmentCategories.map((category) => {
        const equipmentInCategory = [...(currentLocation?.equipment || [])]
          .filter((eq) => eq.category === category)
          .sort((a, b) => a.type.localeCompare(b.type));

        if (equipmentInCategory.length === 0) return null;

        return (
          <div key={category} style={{ marginBottom: "18px" }}>
            <h4
              onClick={() => toggleCategory(category)}
              style={
                mobile
                  ? {
                      cursor: "pointer",
                      fontSize: "18px",
                      padding: "14px 12px",
                      margin: "8px 0",
                      background: "#f9fafb",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }
                  : { cursor: "pointer" }
              }
            >
              {openCategories[category] ? "▼" : "▶"} {category} (
              {equipmentInCategory.length})
            </h4>

            {openCategories[category] && (
              <ul>
                {equipmentInCategory.map((equipment) => (
                  <li key={equipment.id} style={styles.card}>
                    {editingEquipmentId === equipment.id ? (
                      <>
                        <label>Category: </label>
                        <select
                          value={editEquipmentCategory}
                          onChange={(e) => {
                            setEditEquipmentCategory(e.target.value);
                            setEditSubType("");
                          }}
                        >
                          {equipmentCategories.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        <label>Type: </label>
                        <select
                          value={editSubType}
                          onChange={(e) => setEditSubType(e.target.value)}
                        >
                          <option value="">Select Type</option>
                          {(
                            equipmentSubtypes[editEquipmentCategory] || []
                          ).map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                        <input
                          style={styles.input}
                          placeholder="Equipment Name"
                          value={editEquipmentName}
                          onChange={(e) => setEditEquipmentName(e.target.value)}
                        />
                        <input
                          style={styles.input}
                          placeholder="Manufacturer"
                          value={editManufacturer}
                          onChange={(e) => setEditManufacturer(e.target.value)}
                        />
                        <input
                          style={styles.input}
                          placeholder="Model Number"
                          value={editModelNumber}
                          onChange={(e) => setEditModelNumber(e.target.value)}
                        />
                        <input
                          style={styles.input}
                          placeholder="Serial Number"
                          value={editSerialNumber}
                          onChange={(e) => setEditSerialNumber(e.target.value)}
                        />
                        <br />
                        <label>Start-Up Date: </label>
                        <DateWithYearJump
                          value={editStartupDate}
                          onChange={setEditStartupDate}
                        />
                        <br />
                        <label>Last PM Date: </label>
                        <DateWithYearJump
                          value={editLastPmDate}
                          onChange={setEditLastPmDate}
                          yearsBack={10}
                        />
                        <br />
                        <label>PM Frequency Days: </label>
                        <input
                          type="number"
                          value={editPmFrequencyDays}
                          onChange={(e) =>
                            setEditPmFrequencyDays(e.target.value)
                          }
                        />
                        <br />
                        <textarea
                          style={styles.textarea}
                          placeholder="Startup Instructions"
                          value={editStartupInstructions}
                          onChange={(e) =>
                            setEditStartupInstructions(e.target.value)
                          }
                        />
                        <textarea
                          style={styles.textarea}
                          placeholder="Parts List"
                          value={editPartsList}
                          onChange={(e) => setEditPartsList(e.target.value)}
                        />
                        <button style={styles.button} onClick={updateEquipment}>
                          Save
                        </button>
                        <button
                          style={styles.button}
                          onClick={() => {
                            setEditingEquipmentId(null);
                            setEditEquipmentName("");
                          }}
                        >
                          Cancel
                        </button>
                        {canDeleteEquipment && (
                          <button
                            style={{
                              ...styles.button,
                              background: "#fee",
                              color: "#a00",
                              marginLeft: "auto",
                            }}
                            onClick={() => setPendingEquipmentDelete(equipment)}
                          >
                            Delete
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <button
                          style={{
                            ...styles.button,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                          onClick={() => setSelectedEquipment(equipment)}
                          title={
                            equipment.statusNote
                              ? `Status: ${equipment.status || "green"} — ${equipment.statusNote}`
                              : `Status: ${equipment.status || "green"}`
                          }
                        >
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background:
                                equipment.status === "red"
                                  ? "#c12c2c"
                                  : equipment.status === "yellow"
                                  ? "#9a6b00"
                                  : "#1a7f37",
                              flexShrink: 0,
                            }}
                          />
                          {equipment.type} (Records:{" "}
                          {(equipment.serviceRecords || []).length})
                        </button>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: "12px",
                            border: "1px solid #ccc",
                            padding: "10px",
                            marginTop: "6px",
                            borderRadius: "6px",
                            background: "#f9f9f9",
                            maxWidth: "760px",
                          }}
                        >
                          <div>
                            <div>
                              <strong>Category:</strong> {equipment.category}
                            </div>
                            <div>
                              <strong>Type:</strong>{" "}
                              {equipment.subType || "-"}
                            </div>
                            <div>
                              <strong>Manufacturer:</strong>{" "}
                              {equipment.manufacturer || "-"}
                            </div>
                            <div>
                              <strong>Model Number:</strong>{" "}
                              {equipment.modelNumber || "-"}
                            </div>
                            <div>
                              <strong>Serial Number:</strong>{" "}
                              {equipment.serialNumber || "-"}
                            </div>
                            <div>
                              <strong>Start-Up Date:</strong>{" "}
                              {equipment.startupDate || "-"}
                            </div>
                          </div>
                        </div>

                        {canEditEquipment && (
                          <button
                            style={styles.button}
                            onClick={() => startEditingEquipment(equipment)}
                          >
                            Edit
                          </button>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {canAssignStaff && (
        <div style={styles.card}>
          <strong>Assign staff to this building</strong>
          <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
            Facilities managers and maintenance staff only see buildings
            assigned to them. (You, as executive, always see every building.)
          </p>
          {orgUsersErr && (
            <div style={{ color: "red", fontSize: "12px", margin: "4px 0" }}>
              Could not load staff list: {orgUsersErr}
            </div>
          )}
          {orgUsers.filter((u) => !u.deleted && u.role !== "executive")
            .length === 0 ? (
            <span style={{ fontSize: "12px", color: "#666" }}>
              No staff to assign yet.
            </span>
          ) : (
            orgUsers
              .filter((u) => !u.deleted && u.role !== "executive")
              .map((u) => (
                <label
                  key={u.id}
                  style={{ display: "block", fontSize: "13px" }}
                >
                  <input
                    type="checkbox"
                    checked={(currentLocation.assignedUsers || []).includes(
                      u.id
                    )}
                    onChange={() => toggleAssigned(u.id)}
                  />{" "}
                  {u.email} ({u.role})
                </label>
              ))
          )}
        </div>
      )}

      {canShareBuilding && (
        <div style={{ marginTop: "12px" }}>
          <button
            style={styles.button}
            onClick={() => setShowShareBuilding((s) => !s)}
          >
            {showShareBuilding ? "▼ Share Building" : "▶ Share Building"}
          </button>
          {showShareBuilding && (
            <div style={{ ...styles.card, marginTop: "6px" }}>
              <p style={{ fontSize: "12px", color: "#666", margin: "0 0 6px" }}>
                Give this code to a contractor (or facility) so they can link to
                this building and see/log its equipment &amp; service history.
              </p>
              {currentLocation.inviteCode ? (
                <div style={{ fontSize: "13px" }}>
                  Share code: <strong>{currentLocation.inviteCode}</strong>{" "}
                  <button
                    style={styles.button}
                    onClick={() =>
                      actions.updateLocation(currentLocation.id, {
                        inviteCode: genCode(),
                      })
                    }
                  >
                    Regenerate
                  </button>
                </div>
              ) : (
                <button
                  style={styles.button}
                  onClick={() =>
                    actions.updateLocation(currentLocation.id, {
                      inviteCode: genCode(),
                    })
                  }
                >
                  Generate share code
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <TypedConfirmModal
        open={!!pendingEquipmentDelete}
        title="Delete equipment"
        message={
          pendingEquipmentDelete
            ? `${pendingEquipmentDelete.type || "(unnamed)"}${
                pendingEquipmentDelete.subType
                  ? ` (${pendingEquipmentDelete.subType})`
                  : ""
              }\n\nThis CANNOT be undone. All service records, warranties, and parts-list attachments for this equipment will be removed.`
            : ""
        }
        confirmLabel="Delete equipment"
        busy={equipmentDeleteBusy}
        onCancel={() => !equipmentDeleteBusy && setPendingEquipmentDelete(null)}
        onConfirm={async () => {
          if (!pendingEquipmentDelete) return;
          setEquipmentDeleteBusy(true);
          try {
            await actions.deleteEquipment(pendingEquipmentDelete.id);
            // Close edit mode if we just deleted what was being edited
            if (editingEquipmentId === pendingEquipmentDelete.id) {
              setEditingEquipmentId(null);
              setEditEquipmentName("");
            }
            setPendingEquipmentDelete(null);
          } finally {
            setEquipmentDeleteBusy(false);
          }
        }}
      />
    </>
  );
}
